const DATE_ONLY_TIMESTAMP = /^\d{4}-\d{2}-\d{2}$/;
const UTC_NAIVE_TIMESTAMP = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?$/;
const HAS_TIMEZONE = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;
const UNIX_MS_THRESHOLD = 1e12;

function padDatePart(value) {
  return String(value).padStart(2, "0");
}

function formatLocalParts(date, { seconds = true } = {}) {
  const day = [
    date.getFullYear(),
    padDatePart(date.getMonth() + 1),
    padDatePart(date.getDate()),
  ].join("-");
  const time = [
    padDatePart(date.getHours()),
    padDatePart(date.getMinutes()),
    seconds ? padDatePart(date.getSeconds()) : null,
  ].filter((part) => part !== null).join(":");
  return `${day} ${time}`;
}

export function parseTimestamp(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value) || value <= 0) {
      return null;
    }
    const millis = value >= UNIX_MS_THRESHOLD ? value : value * 1000;
    const date = new Date(millis);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const text = String(value || "").trim();
  if (!text) {
    return null;
  }
  if (/^\d+(\.\d+)?$/.test(text)) {
    return parseTimestamp(Number(text));
  }
  if (DATE_ONLY_TIMESTAMP.test(text)) {
    const [year, month, day] = text.split("-").map((part) => Number(part));
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const normalized = UTC_NAIVE_TIMESTAMP.test(text) && !HAS_TIMEZONE.test(text)
    ? `${text.replace(" ", "T")}Z`
    : text;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function toIsoZ(value) {
  const date = parseTimestamp(value);
  if (!date) {
    return "";
  }
  return date.toISOString().replace(/\.\d{3}Z$/, "Z");
}

export function formatDateTime(value, fallback = "-") {
  const date = parseTimestamp(value);
  return date ? formatLocalParts(date, { seconds: true }) : fallback;
}

export function formatCompactDateTime(value, fallback = "") {
  const date = parseTimestamp(value);
  return date ? formatLocalParts(date, { seconds: false }) : fallback;
}

export function formatUtcClock(value, fallback = "-") {
  const date = parseTimestamp(value);
  if (!date) {
    return fallback;
  }
  return `${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`;
}

export function dateOnly(value) {
  const date = parseTimestamp(value);
  if (!date) {
    return "";
  }
  return [
    date.getFullYear(),
    padDatePart(date.getMonth() + 1),
    padDatePart(date.getDate()),
  ].join("-");
}

export function timestampValue(value) {
  const date = parseTimestamp(value);
  return date ? date.getTime() : 0;
}

export function toDatetimeLocalValue(value) {
  const date = parseTimestamp(value);
  if (!date) {
    return "";
  }
  return [
    `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`,
    `T${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`,
  ].join("");
}

export function datetimeLocalToIso(value) {
  const text = String(value || "").trim();
  if (!text) {
    return null;
  }
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return toIsoZ(date);
}

export function localDateStartToIso(value) {
  const text = String(value || "").trim();
  if (!text) {
    return null;
  }
  if (!DATE_ONLY_TIMESTAMP.test(text)) {
    return datetimeLocalToIso(text);
  }
  const [year, month, day] = text.split("-").map((part) => Number(part));
  return toIsoZ(new Date(year, month - 1, day, 0, 0, 0, 0));
}

export function localDateEndToIso(value) {
  const text = String(value || "").trim();
  if (!text) {
    return null;
  }
  if (!DATE_ONLY_TIMESTAMP.test(text)) {
    return datetimeLocalToIso(text);
  }
  const [year, month, day] = text.split("-").map((part) => Number(part));
  return toIsoZ(new Date(year, month - 1, day, 23, 59, 59, 999));
}
